import React, { useState, useEffect } from 'react';

import {
  Pagination as PaginationContainer,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from '@rsc/ui-components/shadcn/Pagination';
import useViewport from '@rsc/ui-components/hooks/useViewport';
import { useUiComponentsInternationalContext } from '@rsc/static-translations';

export function PaginationButton({
  icon,
}: Readonly<{ icon: React.ElementType }>) {
  const Icon = icon;
  return (
    <div className="flex items-center justify-center w-8 h-8 lg:w-10 lg:h-10 rounded-full">
      <Icon className="w-6 h-6 fill-foreground" size="24" />
    </div>
  );
}

interface Props {
  numOfPages: number;
  page?: number;
}

const createArrayFromNumber = n => Array.from({ length: n }, (_, i) => i + 1);

export default function Pagination({ numOfPages, page }: Readonly<Props>) {
  const pagesArray = createArrayFromNumber(numOfPages);
  const [currentPage, setCurrentPage] = useState(null);
  const padding = useViewport() ? 2 : 1;
  const { tt } = useUiComponentsInternationalContext();

  useEffect(() => {
    setCurrentPage(page);
  }, [page]);

  if (typeof window === 'undefined') return null;
  const route = `${window.location.origin}${window.location.pathname}`;

  const firstPageIndex = 0;
  const lastPageIndex = numOfPages - 1;
  const startPage = Math.max(currentPage - padding, firstPageIndex);
  const endPage = Math.min(currentPage + padding, lastPageIndex);

  const visiblePages = Array.from(
    { length: endPage - startPage + 1 },
    (_, i) => startPage + i
  );

  // Helper to determine if ellipses are needed
  const needsEllipsisBefore = startPage > firstPageIndex + 1;
  const needsEllipsisAfter = endPage < lastPageIndex - 1;

  const handlePageChange = (newPage: number, dest: string) => {
    const searchParams = new URLSearchParams(window.location.search);

    if (currentPage === 0 && dest === 'next') {
      searchParams.set('page', newPage.toString());
    } else if (currentPage === 1 && dest === 'previous') {
      searchParams.delete('page');
    } else if (newPage !== -1 && dest === 'previous') {
      searchParams.set('page', currentPage.toString());
    } else {
      searchParams.set('page', (newPage + 1).toString());
    }

    return `${route}?${searchParams.toString()}`;
  };

  return (
    <PaginationContainer>
      <PaginationContent
        className={`${
          window.innerWidth <= 390 ? 'flex-wrap ' : ''
        }justify-center`}
      >
        {/* Previous Button */}
        <PaginationItem>
          <PaginationPrevious
            to={handlePageChange(currentPage - 1, 'previous')}
            disabled={currentPage === 0}
            onClick={e => {
              e.preventDefault();
              if (currentPage !== 1) {
                setCurrentPage(currentPage - 1);
              }
            }}
          />
        </PaginationItem>

        {/* First Page and Ellipsis Before */}
        {startPage > firstPageIndex && (
          <>
            <PaginationItem>
              <PaginationLink
                to={handlePageChange(firstPageIndex, '')}
                isActive={currentPage === firstPageIndex}
                aria-label={tt(s => s.twPaginationComponent.pageAriaLabel, {
                  pageNumber: (firstPageIndex + 1).toString(),
                })}
              >
                {firstPageIndex + 1}
              </PaginationLink>
            </PaginationItem>
            {needsEllipsisBefore && (
              <PaginationItem>
                <PaginationEllipsis />
              </PaginationItem>
            )}
          </>
        )}

        {visiblePages.map(visiblePage => (
          <PaginationItem key={`pagination-item-${visiblePage}`}>
            <PaginationLink
              to={handlePageChange(visiblePage, '')}
              isActive={currentPage === visiblePage}
              aria-label={tt(s => s.twPaginationComponent.pageAriaLabel, {
                pageNumber: (visiblePage + 1).toString(),
              })}
            >
              {visiblePage + 1}
            </PaginationLink>
          </PaginationItem>
        ))}

        {/* Ellipsis After and Last Page */}
        {endPage < lastPageIndex && (
          <>
            {needsEllipsisAfter && (
              <PaginationItem>
                <PaginationEllipsis />
              </PaginationItem>
            )}
            <PaginationItem>
              <PaginationLink
                to={handlePageChange(lastPageIndex, '')}
                isActive={currentPage === lastPageIndex}
                aria-label={tt(s => s.twPaginationComponent.pageAriaLabel, {
                  pageNumber: (lastPageIndex + 1).toString(),
                })}
              >
                {lastPageIndex + 1}
              </PaginationLink>
            </PaginationItem>
          </>
        )}

        {/* Next Button */}
        <PaginationItem>
          <PaginationNext
            to={handlePageChange(
              currentPage === 0 ? currentPage + 2 : currentPage + 1,
              'next'
            )}
            disabled={currentPage === pagesArray.length - 1}
            onClick={e => {
              e.preventDefault();
              setCurrentPage(currentPage + 1);
            }}
          />
        </PaginationItem>
      </PaginationContent>
    </PaginationContainer>
  );
}
